import { Component, inject, signal, output, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MeetingTypeDto } from '@shared/interfaces/dtos/global';
import { MeetingDto } from '@shared/interfaces/dtos/meetings';
import {
  PointsAdvancedSearchAction,
  PointsAdvancedSearchFormGroup,
  PointsAdvancedSearchQuery,
} from '@shared/types/points';
import { PointStatusDto, PointTypeDto } from '@shared/interfaces/dtos/points';
import { ServiceDto } from '@shared/interfaces/dtos/services';
import { MatterDto } from '@shared/interfaces/dtos/matters';
import { TreeNode } from 'primeng/api';
import { PersonalParamDto } from '@shared/interfaces/dtos/params';
import { DecisionTypeDto } from '@shared/interfaces/dtos/decision-types';
import { AldermanshipDto } from '@shared/interfaces/dtos/aldermanships';
import {
  ClassificationCodeDto,
  ClassificationFolderDto,
  ClassificationSheetDto,
} from '@shared/interfaces/dtos/classifications';
import { PointsAdvancedSearchService } from './services/points-advanced-search.service';
import { Notifier } from 'src/app/core/services';
import { NotificationSuccess } from 'src/app/core/models';
import { MyOwnRightsService } from '@shared/services/my-own-rights.service';
import { MyRightsForPoints } from '@shared/models/rights';
import { finalize } from 'rxjs';
import { Meeting } from '@shared/interfaces/meetings';
import { UserDto } from '@shared/interfaces/dtos/users';

@Component({
  selector: 'idel-points-advanced-search',
  templateUrl: './points-advanced-search.component.html',
  styleUrl: './points-advanced-search.component.scss',
})
export class PointsAdvancedSearchComponent implements OnInit {
  private readonly notifier = inject(Notifier);
  private readonly service = inject(PointsAdvancedSearchService);
  private readonly myRightsService = inject(MyOwnRightsService);

  public readonly shouldRefreshSavedSearch = signal<boolean>(false);
  public readonly showNewSavedSearchForm = signal<boolean>(false);
  public readonly rights = signal<MyRightsForPoints>({} as MyRightsForPoints);
  public readonly fetchingRights = signal<boolean>(true);

  public readonly onCancel = output<void>();
  public readonly onSearch = output<PointsAdvancedSearchQuery>();
  public readonly onLoad = output<PersonalParamDto>();
  public readonly refreshDashboard = output<void>();

  public readonly advancedSearchFormGroup = new FormGroup<PointsAdvancedSearchFormGroup>({
    generalData: new FormGroup({
      excludeNotIncludedPoints: new FormControl<boolean>(false),
      includeCancelPoints: new FormControl<boolean>(false),
      myPointsToCorrect: new FormControl<boolean>(false),
      searchIntoContent: new FormControl<boolean>(false),
      onlyUnreads: new FormControl<boolean>(false),
      keyword: new FormControl<string | null>(null),
      pointTypes: new FormControl<PointTypeDto[] | null>(null),
      pointStatuses: new FormControl<PointStatusDto[] | null>(null),
      services: new FormControl<TreeNode<ServiceDto>[] | null>(null),
      matters: new FormControl<MatterDto[] | null>(null),
    }),
    meeting: new FormGroup({
      meetingTypes: new FormControl<MeetingTypeDto[] | null>(null),
      meetings: new FormControl<Meeting[] | null>(null),
      date: new FormControl<[Date, Date] | Date | null>(null),
    }),
    moreData: new FormGroup({
      decisionTypes: new FormControl<DecisionTypeDto[] | null>(null),
      aldermens: new FormControl<AldermanshipDto[] | null>(null),
      classifications: new FormControl<ClassificationCodeDto[] | null>(null),
      folders: new FormControl<ClassificationFolderDto[] | null>(null),
      sheets: new FormControl<ClassificationSheetDto[] | null>(null),
    }),
    agents: new FormGroup({
      agents: new FormControl<UserDto[] | null>(null),
      users: new FormControl<UserDto[] | null>(null),
    }),
  });
  public readonly savedSearchForm = new FormGroup({
    description: new FormControl<string | null>(null, Validators.required),
    order: new FormControl<number | null>(null),
    hideInDashboard: new FormControl<boolean>(false),
  });

  public get isAdvancedSearchFormNotEmpty(): boolean {
    const { value } = this.advancedSearchFormGroup;

    const generalNotEmpty =
      value.generalData.excludeNotIncludedPoints ||
      value.generalData.includeCancelPoints ||
      value.generalData.myPointsToCorrect ||
      value.generalData.searchIntoContent ||
      value.generalData.onlyUnreads ||
      (value.generalData.keyword !== null && value.generalData.keyword.trim() !== '') ||
      value.generalData.pointTypes?.length > 0 ||
      value.generalData.pointStatuses?.length > 0 ||
      value.generalData.services?.length > 0 ||
      value.generalData.matters?.length > 0;

    const meetingSearchNotEmpty =
      value.meeting.meetingTypes?.length > 0 || value.meeting.meetings?.length > 0 || value.meeting.date !== null;

    const moreDataSearchNotEmpty =
      value.moreData.decisionTypes?.length > 0 ||
      value.moreData.aldermens?.length > 0 ||
      value.moreData.classifications?.length > 0 ||
      value.moreData.folders?.length > 0 ||
      value.moreData.sheets?.length > 0;

    const agentsSearchNotEmpty = value.agents.agents?.length > 0 || value.agents.users?.length > 0;

    return generalNotEmpty || meetingSearchNotEmpty || moreDataSearchNotEmpty || agentsSearchNotEmpty;
  }

  ngOnInit(): void {
    this.fetchRights();
  }

  private fetchRights(): void {
    this.fetchingRights.set(true);

    this.myRightsService
      .fetchMyOwnRightsForPoints()
      .pipe(finalize(() => this.fetchingRights.set(false)))
      .subscribe({
        next: (rights) => {
          this.rights.set(rights);
        },
      });
  }

  public searchFromSavedSearch(search: PersonalParamDto): void {
    this.onLoad.emit(search);
  }

  public onActionClick(action: PointsAdvancedSearchAction): void {
    switch (action) {
      case 'save':
        this.showNewSavedSearchForm.set(true);
        this.savedSearchForm.reset();
        break;
      case 'reinit':
        this.advancedSearchFormGroup.reset();
        this.service.shouldPopulateForms.set(false);
        break;
      case 'cancel':
        this.onCancel.emit();
        this.advancedSearchFormGroup.reset();
        break;
      case 'search':
        this.service.shouldPopulateForms.set(false);
        this.onSearch.emit(this.advancedSearchFormGroup.value);
    }
  }

  public onSaveNewSavedSearch(): void {
    const { value } = this.savedSearchForm;

    if (this.savedSearchForm.invalid) return;

    const classificationDetail: [number, number, number] = [-1, -1, -1];

    const { moreData } = this.advancedSearchFormGroup.value;
    const classificationCodes = (moreData?.classifications ?? []).map((classificationCode) => classificationCode.id);
    const folders = (moreData?.folders ?? []).map((folder) => folder.id);
    const sheets = (moreData?.sheets ?? []).map((sheet) => sheet.id);

    if (classificationCodes.length === 1) {
      classificationDetail[0] = classificationCodes[0];
    }

    if (folders.length === 1) {
      classificationDetail[1] = folders[0];
    }

    if (sheets.length === 1) {
      classificationDetail[2] = sheets[0];
    }

    this.service
      .saveSearch(this.advancedSearchFormGroup.value, {
        name: `${value.description}`,
        shownInDashboard: !value.hideInDashboard,
        classification: classificationDetail,
      })
      .subscribe({
        next: () => {
          this.notifier.showSuccess(new NotificationSuccess({ message: 'Recherche sauvée.' }));
          this.showNewSavedSearchForm.set(false);
          this.shouldRefreshSavedSearch.set(true);
          this.refreshDashboard.emit();
        },
      });
  }

  public async populateFormsFromSavedSearch(search: PersonalParamDto): Promise<void> {
    this.service.setCurrentSearch(JSON.parse(search.value));
    console.log(JSON.parse(search.value));

    this.service.shouldPopulateForms.set(true);
  }
}
